import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Switch } from '@/components/ui/switch'
import {
	CAMPAIGN_QUESTION_TYPES,
	type CampaignConditionOperator,
	type CampaignQuestionDraft,
	type CampaignQuestionType,
} from '@/types/campaign'
import { conditionSources, conditionValues } from '@/utils/campaign-draft'

interface CampaignQuestionEditorProps {
	index: number
	total: number
	draft: CampaignQuestionDraft
	drafts: CampaignQuestionDraft[]
	onChange: (draft: CampaignQuestionDraft) => void
	onMove: (direction: -1 | 1) => void
	onRemove: () => void
}

const SCALE_VALUES = Array.from({ length: 11 }, (_, value) => value)

export function CampaignQuestionEditor({
	index,
	total,
	draft,
	drafts,
	onChange,
	onMove,
	onRemove,
}: CampaignQuestionEditorProps) {
	const { t } = useTranslation()
	const isChoice =
		draft.type === 'SINGLE_CHOICE' || draft.type === 'MULTIPLE_CHOICE'
	const sources = conditionSources(drafts, index)
	const source = sources.find(
		(candidate) => candidate.key === draft.condition?.questionKey,
	)

	const changeType = (type: CampaignQuestionType) => {
		const nowChoice = type === 'SINGLE_CHOICE' || type === 'MULTIPLE_CHOICE'
		onChange({
			...draft,
			type,
			options: nowChoice
				? draft.options.length >= 2
					? draft.options
					: ['', '']
				: [],
		})
	}

	const setOption = (optionIndex: number, value: string) =>
		onChange({
			...draft,
			options: draft.options.map((option, i) =>
				i === optionIndex ? value : option,
			),
		})

	return (
		<div className="flex flex-col gap-3 rounded-lg border border-kaefer-light-silver bg-white p-4">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<span className="text-sm font-semibold text-kaefer-dark-grey">
					{t('campaigns.form.questionNumber', { number: index + 1 })}
				</span>
				<div className="flex items-center gap-1">
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="size-8 w-auto"
						disabled={index === 0}
						onClick={() => onMove(-1)}
						aria-label={t('campaigns.form.moveUp')}
					>
						<ArrowUp className="size-4" />
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="size-8 w-auto"
						disabled={index === total - 1}
						onClick={() => onMove(1)}
						aria-label={t('campaigns.form.moveDown')}
					>
						<ArrowDown className="size-4" />
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="size-8 w-auto text-destructive"
						onClick={onRemove}
						aria-label={t('campaigns.form.removeQuestion')}
					>
						<Trash2 className="size-4" />
					</Button>
				</div>
			</div>

			<div className="grid gap-3 sm:grid-cols-[1fr_14rem]">
				<Input
					placeholder={t('campaigns.form.questionLabel')}
					value={draft.label}
					maxLength={500}
					onChange={(e) => onChange({ ...draft, label: e.target.value })}
				/>
				<NativeSelect
					aria-label={t('campaigns.form.questionType')}
					value={draft.type}
					onChange={(e) => changeType(e.target.value as CampaignQuestionType)}
				>
					{CAMPAIGN_QUESTION_TYPES.map((type) => (
						<option key={type} value={type}>
							{t(`campaigns.types.${type}`)}
						</option>
					))}
				</NativeSelect>
			</div>

			<Input
				placeholder={t('campaigns.form.helpText')}
				value={draft.helpText}
				maxLength={1000}
				onChange={(e) => onChange({ ...draft, helpText: e.target.value })}
			/>

			{isChoice && (
				<div className="flex flex-col gap-2">
					{draft.options.map((option, optionIndex) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: opções não têm id e o campo é editado por posição
						<div key={optionIndex} className="flex items-center gap-2">
							<Input
								placeholder={t('campaigns.form.optionPlaceholder', {
									number: optionIndex + 1,
								})}
								value={option}
								maxLength={200}
								onChange={(e) => setOption(optionIndex, e.target.value)}
							/>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="size-8 w-auto shrink-0"
								disabled={draft.options.length <= 2}
								onClick={() =>
									onChange({
										...draft,
										options: draft.options.filter((_, i) => i !== optionIndex),
									})
								}
								aria-label={t('campaigns.form.removeOption')}
							>
								<X className="size-4" />
							</Button>
						</div>
					))}
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="w-auto self-start"
						onClick={() =>
							onChange({ ...draft, options: [...draft.options, ''] })
						}
					>
						<Plus className="size-4" />
						{t('campaigns.form.addOption')}
					</Button>
				</div>
			)}

			{draft.type === 'SCALE' && (
				<div className="flex flex-wrap items-center gap-2 text-sm text-kaefer-dark-grey">
					<span>{t('campaigns.form.scaleFrom')}</span>
					<div className="w-20">
						<NativeSelect
							value={draft.scaleMin}
							onChange={(e) =>
								onChange({ ...draft, scaleMin: Number(e.target.value) })
							}
						>
							{SCALE_VALUES.map((value) => (
								<option key={value} value={value}>
									{value}
								</option>
							))}
						</NativeSelect>
					</div>
					<span>{t('campaigns.form.scaleTo')}</span>
					<div className="w-20">
						<NativeSelect
							value={draft.scaleMax}
							onChange={(e) =>
								onChange({ ...draft, scaleMax: Number(e.target.value) })
							}
						>
							{SCALE_VALUES.map((value) => (
								<option key={value} value={value}>
									{value}
								</option>
							))}
						</NativeSelect>
					</div>
				</div>
			)}

			<div className="flex flex-wrap items-center justify-between gap-3 border-t border-kaefer-light-silver/60 pt-3">
				<label className="flex items-center gap-2 text-sm text-kaefer-dark-grey">
					<Switch
						checked={draft.required}
						onCheckedChange={(required) => onChange({ ...draft, required })}
					/>
					{t('campaigns.form.required')}
				</label>

				{sources.length > 0 && (
					<label className="flex items-center gap-2 text-sm text-kaefer-dark-grey">
						<Switch
							checked={draft.condition !== null}
							onCheckedChange={(enabled) => {
								const first = sources[0]
								onChange({
									...draft,
									condition: enabled
										? {
												questionKey: first.key,
												operator: 'EQUALS',
												value: conditionValues(first)[0] ?? '',
											}
										: null,
								})
							}}
						/>
						{t('campaigns.form.conditional')}
					</label>
				)}
			</div>

			{draft.condition && (
				<div className="grid gap-2 rounded-md bg-kaefer-light-silver/30 p-3 sm:grid-cols-3">
					<NativeSelect
						aria-label={t('campaigns.form.conditionQuestion')}
						value={draft.condition.questionKey}
						onChange={(e) => {
							const next = sources.find(
								(candidate) => candidate.key === e.target.value,
							)
							if (!next || !draft.condition) return
							onChange({
								...draft,
								condition: {
									...draft.condition,
									questionKey: next.key,
									value: conditionValues(next)[0] ?? '',
								},
							})
						}}
					>
						{sources.map((candidate) => (
							<option key={candidate.key} value={candidate.key}>
								{drafts.indexOf(candidate) + 1}.{' '}
								{candidate.label || t('campaigns.form.untitled')}
							</option>
						))}
					</NativeSelect>
					<NativeSelect
						aria-label={t('campaigns.form.conditionOperator')}
						value={draft.condition.operator}
						onChange={(e) =>
							draft.condition &&
							onChange({
								...draft,
								condition: {
									...draft.condition,
									operator: e.target.value as CampaignConditionOperator,
								},
							})
						}
					>
						<option value="EQUALS">{t('campaigns.form.operatorEquals')}</option>
						<option value="NOT_EQUALS">
							{t('campaigns.form.operatorNotEquals')}
						</option>
					</NativeSelect>
					<NativeSelect
						aria-label={t('campaigns.form.conditionValue')}
						value={draft.condition.value}
						onChange={(e) =>
							draft.condition &&
							onChange({
								...draft,
								condition: { ...draft.condition, value: e.target.value },
							})
						}
					>
						{(source ? conditionValues(source) : []).map((value) => (
							<option key={value} value={value}>
								{source?.type === 'BOOLEAN'
									? t(
											value === 'true'
												? 'campaigns.respond.yes'
												: 'campaigns.respond.no',
										)
									: value}
							</option>
						))}
					</NativeSelect>
				</div>
			)}
		</div>
	)
}
